import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaCheckCircle,
  FaEdit,
  FaLayerGroup,
  FaPlus,
  FaRedoAlt,
  FaSave,
  FaSearch,
  FaTags,
  FaTrash,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { isManagementUser, readStoredAdminUser } from './adminAccessControl';
import {
  POS_API_BASE_URL,
  buildApiErrorMessage,
  buildAuthHeaders,
  fetchScopedOutlets,
  getInitialScopedOutlets,
  getBooleanStatusClass,
  getInitials,
  normalizePagedRecords,
  parseJsonResponse,
  resolveAdminSessionToken,
} from './posBackofficeHelpers';
import './PosCatalogManager.css';

const createEmptyCategoryForm = () => ({
  category_code: '',
  category_name: '',
  status: true,
});

function PosCategoriesManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const canAccess = isManagementUser(currentUser);
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const initialOutlets = getInitialScopedOutlets(currentUser);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState(initialOutlets);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId || initialOutlets[0]?.id || '');
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState(createEmptyCategoryForm);
  const [editingId, setEditingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const selectedOutletName = useMemo(
    () => outlets.find(outlet => outlet.id === selectedOutlet)?.name || 'Belum dipilih',
    [outlets, selectedOutlet]
  );

  const filteredCategories = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return categories;
    }

    return categories.filter(category =>
      [category.category_code, category.category_name]
        .some(value => String(value || '').toLowerCase().includes(query))
    );
  }, [categories, search]);

  const categorySummary = useMemo(() => {
    const activeCount = categories.filter(category => Boolean(category.status)).length;
    return {
      total: categories.length,
      active: activeCount,
      inactive: Math.max(categories.length - activeCount, 0),
    };
  }, [categories]);

  const resetForm = useCallback(() => {
    setEditingId('');
    setFormData(createEmptyCategoryForm());
  }, []);

  const fetchCategories = useCallback(async (token, outletId) => {
    if (!outletId || !POS_API_BASE_URL) {
      setCategories([]);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const params = new URLSearchParams({
        outlet_id: outletId,
        limit: '300',
        page: '1',
      });
      const response = await fetch(`${POS_API_BASE_URL}api/categories/details?${params.toString()}`, {
        headers: buildAuthHeaders(token),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat kategori POS.'));
      }

      const { records } = normalizePagedRecords(data);
      setCategories(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error('Failed to fetch POS categories:', error);
      setCategories([]);
      setErrorMessage(error.message || 'Gagal memuat kategori POS.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    let active = true;

    const initialize = async () => {
      try {
        const token = await resolveAdminSessionToken(verifyAndRefreshToken, navigate);
        if (!active || !token) {
          return;
        }

        setAuthToken(token);
        const scopedOutlets = await fetchScopedOutlets({ token, currentUser });
        if (!active) {
          return;
        }

        setOutlets(scopedOutlets);
        setSelectedOutlet(prev => prev || queryOutletId || scopedOutlets[0]?.id || '');
      } catch (error) {
        console.error('Failed to initialize POS categories page:', error);
        if (active) {
          setErrorMessage(error.message || 'Gagal menyiapkan halaman kategori POS.');
        }
      }
    };

    initialize();
    return () => {
      active = false;
    };
  }, [canAccess, currentUser, navigate, queryOutletId, verifyAndRefreshToken]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchCategories(authToken, selectedOutlet);
  }, [authToken, fetchCategories, selectedOutlet]);

  useEffect(() => {
    resetForm();
  }, [resetForm, selectedOutlet]);

  const handleFormChange = event => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    setFormData(prev => ({
      ...prev,
      [target.name]: value,
    }));
  };

  const startEdit = useCallback(category => {
    setEditingId(String(category.id || ''));
    setFormData({
      category_code: String(category.category_code || ''),
      category_name: String(category.category_name || ''),
      status: Boolean(category.status),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        outlet_id: Number(selectedOutlet),
        category_code: formData.category_code.trim().toUpperCase(),
        category_name: formData.category_name.trim(),
        status: Boolean(formData.status),
        icon: '',
      };

      const response = await fetch(
        editingId
          ? `${POS_API_BASE_URL}api/categories/${editingId}`
          : `${POS_API_BASE_URL}api/categories/`,
        {
          method: editingId ? 'PUT' : 'POST',
          headers: buildAuthHeaders(authToken, {
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(payload),
        }
      );
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal menyimpan kategori POS.'));
      }

      await fetchCategories(authToken, selectedOutlet);
      resetForm();
      Swal.fire('Success', editingId ? 'Kategori berhasil diperbarui.' : 'Kategori berhasil dibuat.', 'success');
    } catch (error) {
      console.error('Failed to save POS category:', error);
      Swal.fire('Error', error.message || 'Gagal menyimpan kategori POS.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(async category => {
    const confirmed = await Swal.fire({
      title: 'Hapus kategori?',
      text: `Kategori ${category.category_name || category.category_code || ''} akan dihapus permanen.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#d45463',
    });

    if (!confirmed.isConfirmed) {
      return;
    }

    try {
      const response = await fetch(`${POS_API_BASE_URL}api/categories/${category.id}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(authToken),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal menghapus kategori POS.'));
      }

      if (String(editingId) === String(category.id)) {
        resetForm();
      }
      await fetchCategories(authToken, selectedOutlet);
      Swal.fire('Success', 'Kategori berhasil dihapus.', 'success');
    } catch (error) {
      console.error('Failed to delete POS category:', error);
      Swal.fire('Error', error.message || 'Gagal menghapus kategori POS.', 'error');
    }
  }, [authToken, editingId, fetchCategories, resetForm, selectedOutlet]);

  const columns = useMemo(
    () => [
      {
        name: 'Category',
        sortable: true,
        grow: 1.4,
        cell: row => (
          <div className="table-identity">
            <span className="table-identity__avatar">
              {getInitials(row.category_name || row.category_code || 'CT')}
            </span>
            <div className="table-identity__meta">
              <strong>{row.category_name || '-'}</strong>
              <small>Kode: {row.category_code || '-'}</small>
            </div>
          </div>
        ),
      },
      {
        name: 'Status',
        sortable: true,
        selector: row => (row.status ? 'Active' : 'Inactive'),
        cell: row => (
          <span className={getBooleanStatusClass(Boolean(row.status))}>
            {row.status ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        name: 'Outlet',
        grow: 1.1,
        selector: () => selectedOutletName,
        cell: () => (
          <div className="pos-catalog-value-stack">
            <strong>{selectedOutletName}</strong>
            <small>Outlet aktif</small>
          </div>
        ),
      },
      {
        name: 'Actions',
        width: '180px',
        cell: row => (
          <div className="table-action-group">
            <Button variant="outline-primary" size="sm" onClick={() => startEdit(row)}>
              <FaEdit /> Edit
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row)}>
              <FaTrash /> Hapus
            </Button>
          </div>
        ),
      },
    ],
    [handleDelete, selectedOutletName, startEdit]
  );

  const headerActions = (
    <>
      <Form.Select
        className="accounting-outlet-picker"
        value={selectedOutlet}
        onChange={event => setSelectedOutlet(event.target.value)}
        disabled={outlets.length === 0}
      >
        {outlets.length === 0 ? <option value="">Belum ada outlet</option> : null}
        {outlets.map(outlet => (
          <option key={outlet.id} value={outlet.id}>
            {outlet.name}
          </option>
        ))}
      </Form.Select>
      <Button
        variant="outline-primary"
        onClick={() => fetchCategories(authToken, selectedOutlet)}
        disabled={!authToken || !selectedOutlet || loading}
      >
        <FaRedoAlt /> Refresh
      </Button>
    </>
  );

  const headerMeta = (
    <>
      <span className="status-chip">
        <FaLayerGroup />
        {categorySummary.total} kategori
      </span>
      <span className="status-chip">
        <FaCheckCircle />
        {selectedOutletName}
      </span>
    </>
  );

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaTags}
          eyebrow="Catalog & Inventory"
          title="Catalog Categories"
          description="Kategori dipakai bersama oleh 3 mode POS: jual langsung, produk dengan bahan baku, dan laundry/jasa."
          breadcrumbs={[
            { label: 'Catalog & Inventory' },
            { label: 'Categories' },
          ]}
          actions={headerActions}
          meta={headerMeta}
        />

        <div className="pos-catalog-shell">
          {!POS_API_BASE_URL ? (
            <Alert variant="danger" className="section-card">
              Endpoint POS belum diatur. Pastikan `REACT_APP_API_PRODUCT_URL` tersedia di konfigurasi `adminfitness`.
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="warning" className="section-card">
              {errorMessage}
            </Alert>
          ) : null}

          <div className="pos-catalog-kpi-grid">
            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaTags />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Total kategori</p>
                <strong>{categorySummary.total}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaCheckCircle />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Status aktif</p>
                <strong>{categorySummary.active}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaLayerGroup />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Status nonaktif</p>
                <strong>{categorySummary.inactive}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-note-card">
              <span className="page-eyebrow">Outlet Aktif</span>
              <h2>{selectedOutletName}</h2>
              <p>Kategori dibuat spesifik per outlet agar product master, bahan baku, dan laundry variants tidak tercampur antar cabang.</p>
            </div>
          </div>

          <div className="pos-catalog-layout">
            <section className="surface-card pos-catalog-form-card">
              <div className="pos-catalog-form-head">
                <div>
                  <span className="page-eyebrow">{editingId ? 'Edit' : 'Form Baru'}</span>
                  <h2>{editingId ? 'Perbarui kategori POS' : 'Tambah kategori POS'}</h2>
                  <p>Gunakan kode yang ringkas agar mudah dipakai saat membuat produk baru.</p>
                </div>
                {editingId ? (
                  <Button variant="outline-secondary" onClick={resetForm}>
                    Reset
                  </Button>
                ) : null}
              </div>

              <Form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <Form.Group className="field-stack span-6">
                    <Form.Label>Kode kategori</Form.Label>
                    <Form.Control
                      name="category_code"
                      value={formData.category_code}
                      onChange={handleFormChange}
                      placeholder="Contoh: MKN"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="field-stack span-6">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status ? 'active' : 'inactive'}
                      onChange={event =>
                        setFormData(prev => ({
                          ...prev,
                          status: event.target.value === 'active',
                        }))
                      }
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="field-stack span-12">
                    <Form.Label>Nama kategori</Form.Label>
                    <Form.Control
                      name="category_name"
                      value={formData.category_name}
                      onChange={handleFormChange}
                      placeholder="Nama kategori yang tampil di katalog POS"
                      required
                    />
                  </Form.Group>
                </div>

                <div className="pos-catalog-form-actions mt-3">
                  <Button variant="outline-secondary" type="button" onClick={resetForm}>
                    Reset
                  </Button>
                  <Button type="submit" disabled={saving || !selectedOutlet || !POS_API_BASE_URL}>
                    {editingId ? <FaSave /> : <FaPlus />}
                    {saving ? 'Menyimpan...' : editingId ? 'Update Kategori' : 'Simpan Kategori'}
                  </Button>
                </div>
              </Form>
            </section>

            <section className="surface-card table-card pos-catalog-table-card">
              <div className="pos-catalog-toolbar">
                <div>
                  <span className="page-eyebrow">Data Kategori</span>
                  <h2 className="mb-1">Daftar kategori outlet</h2>
                  <p className="text-muted mb-0">
                    Cari kategori lalu edit langsung tanpa pindah halaman.
                  </p>
                </div>

                <div className="pos-catalog-toolbar__meta">
                  <div className="search-control control-with-icon">
                    <span className="control-with-icon__icon">
                      <FaSearch />
                    </span>
                    <Form.Control
                      value={search}
                      onChange={event => setSearch(event.target.value)}
                      placeholder="Cari kode atau nama kategori"
                    />
                  </div>
                  <span className="pos-catalog-filter-chip">
                    {filteredCategories.length} dari {categories.length} kategori
                  </span>
                </div>
              </div>

              <AdminDataTable
                columns={columns}
                data={filteredCategories}
                progressPending={loading}
                pagination
                persistTableHead
                emptyTitle="Belum ada kategori POS"
                emptyDescription="Mulai dari membuat kategori per outlet agar product dan item POS bisa disusun rapi."
              />
            </section>
          </div>
        </div>
      </Container>
    </>
  );
}

export default PosCategoriesManager;
